import { FC, useState, useCallback } from "react"
import classNames from "classnames"
import { Icon } from "@eventbrite/marmalade"

// icon(s)
import { VimeoPlayButton } from "../Icon/SVGs/VimeoPlayButton"

import * as styles from "./VimeoVideo.module.scss"

const addParameterToURL = (
  url: string,
  paramName: string,
  paramValue: string
) => {
  const urlParts = url.split("?") // Separate base URL and query string (if any)
  const baseUrl = urlParts[0]
  let queryParams = urlParts[1] || "" // Initialize query string

  if (queryParams) {
    queryParams += `&${paramName}=${encodeURIComponent(paramValue)}` // Append new parameter
  } else {
    queryParams = `${paramName}=${encodeURIComponent(paramValue)}` // Create query string with new parameter
  }

  const updatedURL = baseUrl + `?${queryParams}`
  return updatedURL
}

export type Props = {
  src: string
  thumbnailUrl?: string
  caption?: string
  iframeProps?: React.DetailedHTMLProps<
    React.IframeHTMLAttributes<HTMLIFrameElement>,
    HTMLIFrameElement
  >
  className?: string
}

export const VimeoVideo: FC<Props> = ({
  src,
  thumbnailUrl,
  caption,
  className,
  iframeProps = {}
}) => {
  const [finalUrl] = useState(
    src?.replace("https://vimeo.com", "https://player.vimeo.com/video")
  )
  const [isVideoLoadTriggered, setIsVideoLoadTriggered] = useState(false)
  const [imgDimensions, setImgDimensions] = useState<{
    height: number | null
    width: number | null
  }>({
    height: null,
    width: null
  })

  const handleClick = useCallback<
    React.MouseEventHandler<HTMLDivElement>
  >(() => {
    setIsVideoLoadTriggered(!isVideoLoadTriggered)
  }, [isVideoLoadTriggered])

  if (!src) {
    return null
  }
  const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLDivElement>>(
    (event) => {
      if (
        event?.key?.toLowerCase() === "enter" ||
        event?.key?.toLowerCase() === " "
      ) {
        setIsVideoLoadTriggered(!isVideoLoadTriggered)
      }
    },
    [isVideoLoadTriggered]
  )

  return (
    <>
      {caption ? <p style={{ marginBottom: "1rem" }}>{caption}</p> : null}

      <div
        className={classNames(styles?.iframeContainer, className)}
        style={{ position: "relative" }}
      >
        {!isVideoLoadTriggered ? (
          <div className={styles?.thumbnailContainer} onClick={handleClick}>
            <div
              className={styles?.playButton}
              tabIndex={0}
              onKeyDown={handleKeyDown}
            >
              <Icon size="large">
                <VimeoPlayButton />
              </Icon>
            </div>

            <img
              src={thumbnailUrl}
              alt="Vimeo Thumbnail"
              // style={{
              //   height: imgDimensions?.height ?? "360px",
              //   width: imgDimensions?.width ?? "640px"
              // }}
              className={styles.videoThumbnail}
              onLoad={(el) => {
                if (
                  imgDimensions?.height === null ||
                  imgDimensions?.width === null
                ) {
                  setImgDimensions({
                    height: el?.currentTarget?.clientHeight,
                    width: el?.currentTarget?.clientWidth
                  })
                }
              }}
            />
          </div>
        ) : (
          <div>
            <iframe
              src={addParameterToURL(
                addParameterToURL(finalUrl, "autoplay", "1"),
                "muted",
                "1"
              )}
              frameBorder="0"
              allow="allowfullscreen; fullscreen; picture-in-picture; autoplay;"
              style={{
                height: imgDimensions?.height ?? "360px",
                width: imgDimensions?.width ?? "640px",
                maxWidth: "100%"
              }}
              {...iframeProps}
            ></iframe>
          </div>
        )}
      </div>
    </>
  )
}
